/**
 * Cache em memória do status documental por paciente (filtros da lista).
 * Preenchido pela pré-carga STATISTICS ou ao abrir o detalhe.
 * Contabiliza rejected explicitamente (não cai em mixed silencioso).
 * @module patientDocStatusCache
 */

import dayjs from "dayjs";
import type { DocumentReference } from "fhir/r5";
import { extractDocumentVerification } from "src-next/modules/patient-supporting-documents";
import type { DocumentCategory } from "../types";
import {
  groupDocumentsIntoTransactions,
  isHistoricalDocument,
} from "./documentTransactions";
import type { DocumentTransaction } from "./documentTransactions";

export type PatientDocListStatus =
  | "unknown"
  | "none"
  | "pending"
  | "rejected"
  | "all_verified"
  | "mixed";

export interface PatientDocSummary {
  status: PatientDocListStatus;
  /** true se nenhuma das 4 categories tem documento ativo. */
  hasNoDocuments: boolean;
  /** true se alguma category ativa aguarda verificação da org. */
  hasPendingValidation: boolean;
  /** true se existe category obrigatória sem documento, mas o paciente já enviou algum. */
  hasMissingDocuments: boolean;
  /** true se o latest de alguma category foi recusado pela org. */
  hasRejectedDocuments: boolean;
  /** true se as 4 categories obrigatórias estão verificadas (approved). */
  allVerified: boolean;
}

const SUPPORT_CATEGORY_CODES = [
  "anvisa_import_authorization",
  "medical_prescription",
  "personal_document",
  "proof_of_residence",
] as const;

const REQUIRED_CATEGORY_COUNT = SUPPORT_CATEGORY_CODES.length;

const cache = new Map<string, PatientDocSummary>();
const rawDocsCache = new Map<string, DocumentReference[]>();
const transactionCache = new Map<string, DocumentTransaction[]>();
const listeners = new Set<() => void>();

export function subscribePatientDocStatusCache(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyPatientDocStatusCache(): void {
  listeners.forEach((listener) => listener());
}

function mergeIncomingDocuments(
  incoming: DocumentReference[],
  existing: DocumentReference[] | undefined,
  organizationId?: string,
): DocumentReference[] {
  if (!existing?.length) return incoming;
  const existingById = new Map(
    existing
      .filter((doc) => doc.id)
      .map((doc) => [doc.id as string, doc]),
  );

  return incoming.map((doc) => {
    if (!doc.id) return doc;
    const previous = existingById.get(doc.id);
    if (!previous) return doc;
    const incomingStatus = extractDocumentVerification(
      doc,
      organizationId,
    ).status;
    const previousStatus = extractDocumentVerification(
      previous,
      organizationId,
    ).status;
    const previousResolved =
      previousStatus === "approved" || previousStatus === "rejected";
    const incomingUnresolved =
      incomingStatus !== "approved" && incomingStatus !== "rejected";
    return previousResolved && incomingUnresolved ? previous : doc;
  });
}

function dateValue(iso?: string): number {
  if (!iso) return 0;
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.valueOf() : 0;
}

function pickLatest(docs: DocumentReference[]): DocumentReference | undefined {
  if (docs.length <= 1) return docs[0];
  return [...docs].sort(
    (left, right) => dateValue(right.date) - dateValue(left.date),
  )[0];
}

function categoryCodeOf(doc: DocumentReference): string | undefined {
  return (
    doc.category?.[0]?.coding?.[0]?.code ||
    doc.category?.[0]?.text ||
    undefined
  );
}

function writeSummary(
  key: string,
  withDocs: number,
  pending: number,
  approved: number,
  rejected: number,
): void {
  const hasNoDocuments = withDocs === 0;
  const hasPendingValidation = pending > 0;
  const hasMissingDocuments =
    withDocs > 0 && withDocs < REQUIRED_CATEGORY_COUNT;
  const hasRejectedDocuments = rejected > 0;
  const allVerified =
    approved === REQUIRED_CATEGORY_COUNT &&
    pending === 0 &&
    rejected === 0;

  let status: PatientDocListStatus = "mixed";
  if (hasNoDocuments) status = "none";
  else if (allVerified) status = "all_verified";
  else if (hasPendingValidation || hasMissingDocuments) status = "pending";
  else if (hasRejectedDocuments) status = "rejected";

  cache.set(key, {
    status,
    hasNoDocuments,
    hasPendingValidation,
    hasMissingDocuments,
    hasRejectedDocuments,
    allVerified,
  });
}

function classifyLatest(
  latest: DocumentReference,
  organizationId: string | undefined,
  counters: { pending: number; approved: number; rejected: number },
): void {
  const verification = extractDocumentVerification(latest, organizationId);
  if (verification.status === "approved") {
    counters.approved += 1;
    return;
  }
  if (verification.status === "rejected") {
    counters.rejected += 1;
    return;
  }
  counters.pending += 1;
}

export function rememberPatientDocSummary(
  patientKey: string,
  categories: DocumentCategory[],
  organizationId?: string,
): void {
  const key = patientKey.trim();
  if (!key) return;

  const byCode = new Map(
    categories.map((category) => [category.code, category]),
  );
  const counters = { pending: 0, approved: 0, rejected: 0 };
  let withDocs = 0;

  for (const code of SUPPORT_CATEGORY_CODES) {
    const latest = pickLatest(byCode.get(code)?.docs ?? []);
    if (!latest) continue;
    withDocs += 1;
    classifyLatest(latest, organizationId, counters);
  }

  writeSummary(
    key,
    withDocs,
    counters.pending,
    counters.approved,
    counters.rejected,
  );

  const flatDocs = categories.flatMap((category) => [
    ...(category.docs ?? []),
    ...(category.cancelledDocs ?? []),
  ]);
  rawDocsCache.set(key, flatDocs);
  transactionCache.set(key, groupDocumentsIntoTransactions(flatDocs));
  notifyPatientDocStatusCache();
}

/**
 * Hidrata o cache a partir de DocumentReferences flat (batch STATISTICS).
 * Status sempre filtrado pelo attester/rejeição da organizationId da sessão.
 */
export function rememberPatientDocsFromRaw(
  patientKey: string,
  docs: DocumentReference[],
  organizationId?: string,
): void {
  const key = patientKey.trim();
  if (!key) return;

  const merged = mergeIncomingDocuments(
    docs,
    rawDocsCache.get(key),
    organizationId,
  );

  const byCategory = new Map<string, DocumentReference[]>();
  for (const doc of merged) {
    if (isHistoricalDocument(doc)) continue;
    const code = categoryCodeOf(doc);
    if (!code || !(SUPPORT_CATEGORY_CODES as readonly string[]).includes(code)) {
      continue;
    }
    const list = byCategory.get(code) ?? [];
    list.push(doc);
    byCategory.set(code, list);
  }

  const counters = { pending: 0, approved: 0, rejected: 0 };
  let withDocs = 0;

  for (const code of SUPPORT_CATEGORY_CODES) {
    const latest = pickLatest(byCategory.get(code) ?? []);
    if (!latest) continue;
    withDocs += 1;
    classifyLatest(latest, organizationId, counters);
  }

  writeSummary(
    key,
    withDocs,
    counters.pending,
    counters.approved,
    counters.rejected,
  );
  rawDocsCache.set(key, merged);
  transactionCache.set(key, groupDocumentsIntoTransactions(merged));
  notifyPatientDocStatusCache();
}

/** Marca pacientes sem docs na resposta do batch (hasNoDocuments). */
export function rememberEmptyPatientDocSummary(patientKey: string): void {
  const key = patientKey.trim();
  if (!key) return;
  if ((rawDocsCache.get(key)?.length ?? 0) > 0) return;
  writeSummary(key, 0, 0, 0, 0);
  rawDocsCache.set(key, []);
  transactionCache.set(key, groupDocumentsIntoTransactions([]));
  notifyPatientDocStatusCache();
}

export function peekPatientDocSummary(
  patientKey: string,
): PatientDocSummary | undefined {
  return cache.get(patientKey.trim());
}

export function peekPatientRawDocs(
  patientKey: string,
): DocumentReference[] | undefined {
  return rawDocsCache.get(patientKey.trim());
}

export function peekPatientTransactions(
  patientKey: string,
): DocumentTransaction[] {
  return transactionCache.get(patientKey.trim()) ?? [];
}
